import { addDays, fromIsoDay, isoDay } from 'helper/timeLabels';

/**
 * The CRM side of the companies list, which Jobbiz does not store yet: where
 * a company stands (prospect, client or lost), since when it is a client, why
 * it was lost and when to try again, its sales rep, its recruiter and its next
 * action. The rest of a row is the real company of GET admin/getCompaniesList.
 *
 * The demo draws these fields from the company's id, so a company keeps the
 * same ones from one load to the next; only `missionCount` comes from the real
 * company (a company with missions is a client). The api will store them and
 * answer the shape of `companiesView`.
 */

export const COMPANY_TABS = ['prospects', 'new', 'old', 'lost'];
export const COMPANIES_PER_PAGE = 20;
// A client signed less than 90 days ago is a new one.
export const NEW_CLIENT_DAYS = 90;
export const NO_CRM_FILTERS = { action: 'all', commercial: 'all' };

const DAY = 86400000;

const LOST_REASONS = [
  'Prix trop élevé',
  'A choisi une autre agence',
  'Pas de besoin avant la saison',
  'Recrute en direct',
  'Injoignable depuis 3 mois',
  'Établissement fermé',
];
const PROSPECT_TASKS = ['Envoyer la plaquette tarifaire', 'Relancer la proposition', 'Envoyer le devis'];
const CLIENT_TASKS = ['Mettre à jour la fiche client', 'Préparer le bilan trimestriel', 'Envoyer le contrat cadre'];

// FNV-1a: the same number for the same text, at every load.
export const hashOf = (text) => {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};
// A number below `size` for this record; each salt draws its own.
export const draw = (record, salt, size) => hashOf(`${salt}:${record._id}`) % size;
// The later of two days written « 2026-10-21 »: nothing happens to a company
// before it joined.
const laterDay = (day, other) => (day > other ? day : other);

// Its next callback, appointment or task, within the coming week.
const nextActionOf = (company, client, now) => {
  if (draw(company, 'action', 100) >= 45) return null;
  const at = addDays(now, draw(company, 'action-day', 6));
  at.setHours(8 + draw(company, 'action-hour', 9), draw(company, 'action-minutes', 2) * 30, 0, 0);
  const kind = draw(company, 'action-kind', 3);
  if (kind === 0) return { kind: 'callback', at: at.toISOString() };
  if (kind === 1) {
    const category = client ? ['visite_client', 'rdv_client'][draw(company, 'action-category', 2)] : 'prospection';
    return { kind: 'appointment', category, at: at.toISOString() };
  }
  const titles = client ? CLIENT_TASKS : PROSPECT_TASKS;
  return { kind: 'task', title: titles[draw(company, 'action-title', titles.length)], dueOn: isoDay(at) };
};

/**
 * The CRM fields of a real company. `meId` is the signed-in user, who gets a
 * third of the companies; `team` the people a company can be assigned to.
 */
export const crmFieldsOf = (company, { meId, team, now }) => {
  const client = company.missionCount > 0;
  const lost = draw(company, 'lost', client ? 10 : 6) === 0;
  const signedLately = client && draw(company, 'new', 12) === 0;
  const joined = company.createdAt ? isoDay(new Date(company.createdAt)) : isoDay(now);
  const others = team.filter((person) => person.id !== meId);
  const commercialId =
    draw(company, 'commercial', 3) === 0 || !others.length
      ? meId
      : others[draw(company, 'colleague', others.length)].id;
  const recruiterId =
    client && team.length && draw(company, 'recruiter', 5) >= 2 ? team[draw(company, 'recruiter-who', team.length)].id : null;
  const relaunches = lost && draw(company, 'relaunch', 5) < 3;
  return {
    stage: lost ? 'lost' : client ? 'client' : 'prospect',
    clientSince: client
      ? signedLately
        ? laterDay(isoDay(addDays(now, -(3 + draw(company, 'since', 80)))), joined)
        : joined
      : null,
    lostAt: lost ? laterDay(isoDay(addDays(now, -(5 + draw(company, 'lost-at', 150)))), joined) : null,
    lostReason: lost ? LOST_REASONS[draw(company, 'reason', LOST_REASONS.length)] : null,
    relaunchOn: relaunches
      ? isoDay(new Date(now.getFullYear(), now.getMonth() + 1 + draw(company, 'relaunch-month', 6), 1))
      : null,
    commercialId,
    recruiterId,
    nextAction: lost ? null : nextActionOf(company, client, now),
  };
};

// The real companies with their CRM fields; `overrides` holds what was
// changed since (an assignment), by company id.
export const withCrmFields = (companies, { meId, team, now, overrides = {} }) =>
  companies.map((company) => ({
    ...company,
    ...crmFieldsOf(company, { meId, team, now }),
    ...overrides[company._id],
  }));

export const tabOf = (company, now) => {
  if (company.stage === 'lost') return 'lost';
  if (company.stage === 'prospect') return 'prospects';
  return (now - fromIsoDay(company.clientSince)) / DAY <= NEW_CLIENT_DAYS ? 'new' : 'old';
};

export const keepCompany = (company, { action = 'all', commercial = 'all' } = {}) =>
  (action === 'all' || (action === 'with') === Boolean(company.nextAction)) &&
  (commercial === 'all' || company.commercialId === commercial);

// Prospects come from the latest created, clients from the latest signed,
// lost companies from the latest lost.
const latest = (key) => (a, b) => String(b[key] ?? '').localeCompare(String(a[key] ?? ''));
const ORDERS = {
  prospects: latest('createdAt'),
  new: latest('clientSince'),
  old: latest('clientSince'),
  lost: latest('lostAt'),
};

/**
 * A page of a tab, as the api will answer it: the companies, how many the tab
 * holds, the count of every tab, and the page (from 1) out of how many.
 */
export const companiesView = (companies, { tab, filters, page = 1, limit = COMPANIES_PER_PAGE, now }) => {
  const byTab = Object.fromEntries(COMPANY_TABS.map((id) => [id, []]));
  companies.filter((company) => keepCompany(company, filters)).forEach((company) => byTab[tabOf(company, now)].push(company));
  const list = [...byTab[tab]].sort(ORDERS[tab]);
  const pages = Math.max(1, Math.ceil(list.length / limit));
  const current = Math.min(Math.max(1, page), pages);
  return {
    companies: list.slice((current - 1) * limit, current * limit),
    count: list.length,
    counts: Object.fromEntries(COMPANY_TABS.map((id) => [id, byTab[id].length])),
    page: current,
    pages,
    perPage: limit,
  };
};
